import React, { useState, useEffect } from 'react';
import { virtualAdvisorAdminService, KnowledgeStatusMetrics } from '../services/virtualAdvisorAdminService';
import {
  Card,
  Tabs,
  Table,
  Button,
  Input,
  Select,
  Upload,
  Modal,
  Form,
  InputNumber,
  Tag,
  Space,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Divider,
  Descriptions,
  Badge,
  Tooltip,
  Alert,
  Progress,
  Drawer,
  Checkbox,
  Radio,
  List,
  Avatar,
  Statistic,
  Empty
} from 'antd';
import {
  UploadOutlined,
  FileTextOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SettingOutlined,
  ExportOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  FileSearchOutlined,
  CommentOutlined,
  BugOutlined,
  LikeOutlined,
  DislikeOutlined
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { UploadProps } from 'antd';

const { TabPane } = Tabs;
const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface Document {
  DocumentID: number;
  Title: string;
  FileName: string;
  FilePath: string;
  FileSize: number;
  MimeType: string;
  Status: 'uploaded' | 'processing' | 'processed' | 'error' | 'awaiting_review';
  CreatedAt: string;
  ProcessedAt?: string;
  ErrorMessage?: string;
  GoogleFileId?: string;
  FileSearchStoreName?: string;
  FileSearchUploadedAt?: string;
}

interface Knowledge {
  KnowledgeID: number;
  Title: string;
  Content: string;
  Category: string;
  DocumentID?: number;
  Status: 'draft' | 'pending_review' | 'published';
  CreatedBy: number;
  CreatedAt: string;
  ApprovedBy?: number;
  ApprovedAt?: string;
  Notes?: string;
  Tags: string[];
}

interface Tag {
  TagID: number;
  Name: string;
  CreatedAt: string;
}

interface FAQ {
  FAQID: number;
  Category: string;
  Question: string;
  Answer: string;
  Status: 'draft' | 'published';
  CreatedAt: string;
  UpdatedAt: string;
  UpdatedBy: number;
}

interface DocumentChunk {
  ChunkID: number;
  DocumentID: number;
  Content: string;
  PageNumber: number;
  ChunkIndex: number;
}

type KnowledgeMetricsState = KnowledgeStatusMetrics & { total: number };

const DEFAULT_KNOWLEDGE_METRICS: KnowledgeMetricsState = {
  draft: 0,
  pending_review: 0,
  published: 0,
  total: 0
};

const VirtualAdvisorManagementPage: React.FC = () => {
  // All hooks must be at the top level, not inside try-catch
  const [activeTab, setActiveTab] = useState('documents');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [knowledge, setKnowledge] = useState<Knowledge[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [settings, setSettings] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [selectedKnowledge, setSelectedKnowledge] = useState<Knowledge | null>(null);
  const [documentChunks, setDocumentChunks] = useState<DocumentChunk[]>([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [playgroundVisible, setPlaygroundVisible] = useState(false);
  const [isKnowledgeEditing, setIsKnowledgeEditing] = useState(false);
  const [knowledgeMetrics, setKnowledgeMetrics] = useState<KnowledgeMetricsState>(DEFAULT_KNOWLEDGE_METRICS);
  const [knowledgeTotal, setKnowledgeTotal] = useState(0);

  // Form states
  const [documentForm] = Form.useForm();
  const [knowledgeForm] = Form.useForm();
  const [faqForm] = Form.useForm();
  const [tagForm] = Form.useForm();
  const [settingsForm] = Form.useForm();
  const [playgroundForm] = Form.useForm();

  // Filters and pagination
  const [documentFilters, setDocumentFilters] = useState({ status: '', search: '' });
  const [knowledgeFilters, setKnowledgeFilters] = useState({ status: '', category: '', search: '' });
  const [faqFilters, setFaqFilters] = useState({ status: '', category: '' });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  // New tabs state
  const [pendingKnowledge, setPendingKnowledge] = useState<any[]>([]);
  const [chatLogs, setChatLogs] = useState<any[]>([]);
  const [debugSearchQuery, setDebugSearchQuery] = useState('');
  const [debugSearchResults, setDebugSearchResults] = useState<any[]>([]);
  const [loadingDebugSearch, setLoadingDebugSearch] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'documents':
          await loadDocuments();
          break;
        case 'knowledge':
          await loadDocuments();
          await Promise.all([
            loadKnowledge(),
            loadTags(),
            loadKnowledgeMetrics()
          ]);
          break;
        case 'faqs':
          await loadFAQs();
          break;
        case 'settings':
          await loadSettings();
          break;
        case 'approval':
          await loadPendingKnowledge();
          break;
        case 'chatlogs':
          await loadChatLogs();
          break;
        case 'debugsearch':
          // Debug search loads on demand
          break;
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadDocuments = async () => {
    try {
      const result = await virtualAdvisorAdminService.getDocuments(documentFilters, {
        page: pagination.current,
        size: pagination.pageSize
      });
      if (result.success) {
        setDocuments(result.data);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const loadKnowledge = async (
    filters: typeof knowledgeFilters = knowledgeFilters,
    paging: typeof pagination = pagination
  ) => {
    try {
      const result = await virtualAdvisorAdminService.getKnowledge(filters, {
        page: paging.current,
        size: paging.pageSize
      });
      console.log('getKnowledge result:', result);
      if (result.success) {
        setKnowledge(result.data || []);
        setKnowledgeTotal(result.total || (result.data?.length || 0));
      }
    } catch (error) {
      console.error('Error loading knowledge:', error);
    }
  };

  const loadKnowledgeMetrics = async () => {
    try {
      const result = await virtualAdvisorAdminService.getKnowledgeStatusMetrics();
      if (result.success) {
        const metrics = result.data || { draft: 0, pending_review: 0, published: 0 };
        const total = result.total ?? metrics.draft + metrics.pending_review + metrics.published;
        setKnowledgeMetrics({
          draft: metrics.draft ?? 0,
          pending_review: metrics.pending_review ?? 0,
          published: metrics.published ?? 0,
          total
        });
      }
    } catch (error) {
      console.error('Error loading knowledge metrics:', error);
      setKnowledgeMetrics({ ...DEFAULT_KNOWLEDGE_METRICS });
    }
  };

  const applyKnowledgeStatusFilter = (statusValue: string) => {
    const nextFilters = { ...knowledgeFilters, status: statusValue };
    const nextPagination = { ...pagination, current: 1 };
    setKnowledgeFilters(nextFilters);
    setPagination(nextPagination);
    loadKnowledge(nextFilters, nextPagination);
  };

  const handleKnowledgeSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const nextFilters = { ...knowledgeFilters, search: value };
    const nextPagination = { ...pagination, current: 1 };
    setKnowledgeFilters(nextFilters);
    setPagination(nextPagination);
    loadKnowledge(nextFilters, nextPagination);
  };

  const handleKnowledgeCategoryChange = (value: string) => {
    const nextFilters = { ...knowledgeFilters, category: value };
    const nextPagination = { ...pagination, current: 1 };
    setKnowledgeFilters(nextFilters);
    setPagination(nextPagination);
    loadKnowledge(nextFilters, nextPagination);
  };

  const handleKnowledgeTableChange = (tablePagination: TablePaginationConfig) => {
    const nextPagination = {
      current: tablePagination?.current || 1,
      pageSize: tablePagination?.pageSize || pagination.pageSize
    };
    setPagination(nextPagination);
    loadKnowledge(knowledgeFilters, nextPagination);
  };

  const loadTags = async () => {
    try {
      const result = await virtualAdvisorAdminService.getTags();
      if (result.success) {
        setTags(result.data);
      }
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const loadPendingKnowledge = async () => {
    try {
      const result = await virtualAdvisorAdminService.getKnowledge({ status: 'pending_review' }, { page: 1, size: 100 });
      if (result.success) {
        setPendingKnowledge(result.data || []);
      }
    } catch (error) {
      console.error('Error loading pending knowledge:', error);
      message.error('Không thể tải danh sách kiến thức chờ duyệt');
    }
  };

  const loadChatLogs = async () => {
    try {
      // TODO: Implement API call to get chat logs with feedback
      // const result = await virtualAdvisorAdminService.getChatLogs();
      // setChatLogs(result.data);
      
      // Mock data for now
      setChatLogs([
        {
          id: 1,
          sessionId: 101,
          studentName: 'Nguyễn Văn A',
          message: 'Học phí năm học này là bao nhiêu?',
          response: 'Học phí năm học 2024-2025 dao động từ 8-12 triệu đồng tùy theo ngành học...',
          confidence: 0.85,
          model: 'gemini-1.5-flash',
          sources: [{ id: 'knowledge-123', title: 'Quy định học phí', sourceNumber: 1 }],
          feedback: 'positive',
          createdAt: new Date('2025-10-20T10:30:00')
        },
        {
          id: 2,
          sessionId: 102,
          studentName: 'Trần Thị B',
          message: 'Làm thế nào để xin giấy xác nhận sinh viên?',
          response: 'Để xin giấy xác nhận sinh viên, bạn cần nộp đơn tại phòng đào tạo...',
          confidence: 0.92,
          model: 'gemini-1.5-flash',
          sources: [{ id: 'knowledge-456', title: 'Thủ tục hành chính', sourceNumber: 1 }],
          feedback: 'positive',
          createdAt: new Date('2025-10-20T14:15:00')
        },
        {
          id: 3,
          sessionId: 103,
          studentName: 'Lê Văn C',
          message: 'Điều kiện thi lại là gì?',
          response: 'Sinh viên được phép thi lại khi đạt điểm dưới 4.0...',
          confidence: 0.65,
          model: 'gemini-1.5-flash',
          sources: [],
          feedback: 'negative',
          createdAt: new Date('2025-10-19T09:45:00')
        }
      ]);
    } catch (error) {
      console.error('Error loading chat logs:', error);
      message.error('Không thể tải lịch sử chat');
    }
  };

  const handleDebugSearch = async () => {
    if (!debugSearchQuery.trim()) {
      message.warning('Vui lòng nhập truy vấn tìm kiếm');
      return;
    }

    setLoadingDebugSearch(true);
    try {
      // TODO: Implement API call for debug search
      // const result = await virtualAdvisorAdminService.debugSearch(debugSearchQuery);
      
      // Mock data for now
      setTimeout(() => {
        setDebugSearchResults([
          {
            knowledgeId: 123,
            title: 'Quy định học phí năm học 2024-2025',
            category: 'Học phí',
            score: 0.92,
            keywordScore: 0.85,
            similarityScore: 0.95,
            content: 'Học phí năm học 2024-2025 dao động từ 8-12 triệu đồng...',
            tags: ['học phí', 'quy định', 'năm học']
          },
          {
            knowledgeId: 456,
            title: 'Chính sách miễn giảm học phí',
            category: 'Học phí',
            score: 0.78,
            keywordScore: 0.75,
            similarityScore: 0.80,
            content: 'Sinh viên thuộc diện chính sách ưu đãi được miễn giảm học phí...',
            tags: ['học phí', 'miễn giảm', 'chính sách']
          }
        ]);
        setLoadingDebugSearch(false);
      }, 1000);
    } catch (error) {
      console.error('Error debug search:', error);
      message.error('Không thể thực hiện tìm kiếm');
      setLoadingDebugSearch(false);
    }
  };

  const loadFAQs = async () => {
    try {
      const result = await virtualAdvisorAdminService.getFAQs(faqFilters, {
        page: pagination.current,
        size: pagination.pageSize
      });
      console.log('getFAQs result:', result);
      if (result.success) {
        setFaqs(result.data || []);
      }
    } catch (error) {
      console.error('Error loading FAQs:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const result = await virtualAdvisorAdminService.getSettings();
      if (result.success) {
        setSettings(result.data);
        settingsForm.setFieldsValue(result.data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadDocumentChunks = async (documentId: number) => {
    try {
      const result = await virtualAdvisorAdminService.getDocumentChunks(documentId);
      if (result.success) {
        setDocumentChunks(result.data);
      }
    } catch (error) {
      console.error('Error loading document chunks:', error);
      setDocumentChunks([]);
    }
  };

  const handleDocumentUpload = async (options: any) => {
    const { file, onSuccess, onError } = options;

    try {
      const result = await virtualAdvisorAdminService.uploadDocument(file, file.name);

      if (result.success) {
        message.success(result.message);
        loadDocuments();
        onSuccess(result);
      } else {
        message.error(result.message);
        onError(result.message);
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi tải lên file');
      onError(error);
    }
  };

  const handleProcessDocument = async (documentId: number) => {
    try {
      const result = await virtualAdvisorAdminService.processDocument(documentId);

      if (result.success) {
        message.success(result.message);
        loadDocuments();
        loadKnowledge();
        loadKnowledgeMetrics();
      } else {
        // Show detailed error with instructions
        Modal.error({
          title: 'Không thể xử lý tài liệu',
          content: (
            <div>
              <p>{result.message}</p>
              {result.message.includes('OCR') || result.message.includes('scan') ? (
                <div style={{ marginTop: 16, padding: 12, background: '#f0f2f5', borderRadius: 8 }}>
                  <p><strong>💡 Giải pháp:</strong></p>
                  <ul style={{ marginLeft: 20 }}>
                    <li>Upload lại file PDF từ Word/Google Docs (File → Save as PDF)</li>
                    <li>Sử dụng công cụ OCR online: <a href="https://www.onlineocr.net/" target="_blank" rel="noopener noreferrer">OnlineOCR.net</a></li>
                    <li>Liên hệ quản trị viên để cấu hình OCR</li>
                  </ul>
                  <p style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                    Xem thêm: backend/PDF_GUIDE.md
                  </p>
                </div>
              ) : null}
            </div>
          ),
          width: 600
        });
        loadDocuments();
        loadKnowledge();
        loadKnowledgeMetrics();
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi xử lý tài liệu');
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      const result = await virtualAdvisorAdminService.deleteDocument(documentId);

      if (result.success) {
        message.success(result.message);
        loadDocuments();
        
        // Close drawer if the deleted document was being viewed
        if (selectedDocument?.DocumentID === documentId) {
          setDrawerVisible(false);
          setSelectedDocument(null);
        }
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi xóa tài liệu');
    }
  };

  const handleSyncToFileSearch = async (documentId: number) => {
    try {
      message.loading({ content: 'Đang đồng bộ lên File Search...', key: 'sync', duration: 0 });
      
      const result = await virtualAdvisorAdminService.syncDocumentToFileSearch(documentId);

      if (result.success) {
        message.success({ content: result.message || 'Đã đồng bộ tài liệu lên File Search thành công', key: 'sync' });
        loadDocuments();
      } else {
        message.error({ content: result.message || 'Không thể đồng bộ tài liệu', key: 'sync' });
      }
    } catch (error) {
      message.error({ content: 'Có lỗi xảy ra khi đồng bộ tài liệu lên File Search', key: 'sync' });
      console.error('Sync to File Search error:', error);
    }
  };

  const openCreateKnowledgeModal = () => {
    setIsKnowledgeEditing(false);
    setSelectedKnowledge(null);
    knowledgeForm.resetFields();
    knowledgeForm.setFieldsValue({
      status: 'pending_review',
      tags: [],
      documentId: undefined,
      notes: ''
    });
    setModalVisible(true);
  };

  const openEditKnowledgeModal = (record: Knowledge) => {
    setIsKnowledgeEditing(true);
    setSelectedKnowledge(record);
    knowledgeForm.setFieldsValue({
      title: record.Title,
      category: record.Category,
      content: record.Content,
      tags: record.Tags,
      documentId: record.DocumentID,
      status: record.Status,
      notes: record.Notes || ''
    });
    setModalVisible(true);
  };

  const closeKnowledgeModal = () => {
    setModalVisible(false);
    setIsKnowledgeEditing(false);
    setSelectedKnowledge(null);
    knowledgeForm.resetFields();
  };

  const handleSubmitKnowledge = async (values: any) => {
    const payload = {
      title: values.title,
      category: values.category,
      content: values.content,
      documentId: values.documentId ? Number(values.documentId) : undefined,
      tags: Array.isArray(values.tags) ? values.tags : undefined,
      status: values.status || 'pending_review',
      notes: values.notes,
    };

    try {
      let result: { success: boolean; message: string; knowledgeId?: number };

      if (isKnowledgeEditing && selectedKnowledge) {
        result = await virtualAdvisorAdminService.updateKnowledge(selectedKnowledge.KnowledgeID, payload);
      } else {
        result = await virtualAdvisorAdminService.createKnowledge(payload);
      }

      if (result.success) {
  message.success(result.message || (isKnowledgeEditing ? 'Đã cập nhật kiến thức' : 'Đã tạo kiến thức'));
  closeKnowledgeModal();
        loadKnowledge();
        loadKnowledgeMetrics();
      } else {
        message.error(result.message || 'Không thể lưu kiến thức');
      }
    } catch (error) {
      message.error(isKnowledgeEditing ? 'Có lỗi xảy ra khi cập nhật kiến thức' : 'Có lỗi xảy ra khi tạo kiến thức');
    }
  };

  const handlePublishKnowledge = async (knowledgeId: number) => {
    try {
      const result = await virtualAdvisorAdminService.publishKnowledge(knowledgeId, 'Published from admin interface');

      if (result.success) {
        message.success(result.message);
        loadKnowledge();
        loadKnowledgeMetrics();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi xuất bản kiến thức');
    }
  };

  const handleCreateTag = async (values: any) => {
    try {
      const result = await virtualAdvisorAdminService.createTag(values.name);

      if (result.success) {
        message.success(result.message);
        tagForm.resetFields();
        loadTags();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi tạo tag');
    }
  };

  const handleDeleteTag = async (tagId: number) => {
    try {
      const result = await virtualAdvisorAdminService.deleteTag(tagId);

      if (result.success) {
        message.success(result.message);
        loadTags();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi xóa tag');
    }
  };

  const handleUpdateSettings = async (values: any) => {
    try {
      const result = await virtualAdvisorAdminService.updateSettings(values);

      if (result.success) {
        message.success(result.message);
        loadSettings();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi cập nhật cài đặt');
    }
  };

  const handlePlaygroundAsk = async (values: any) => {
    try {
      const result = await virtualAdvisorAdminService.playgroundAsk(values);

      if (result.success) {
        Modal.success({
          title: 'Câu trả lời từ AI',
          content: (
            <div>
              <p><strong>Câu hỏi:</strong> {values.question}</p>
              <p><strong>Trả lời:</strong></p>
              <div style={{ background: '#f6f8fa', padding: '10px', borderRadius: '4px', maxHeight: '300px', overflowY: 'auto' }}>
                {result.answer}
              </div>
              <p><strong>Độ tin cậy:</strong> {Math.round(result.confidence * 100)}%</p>
              {result.context && (
                <p><strong>Context được sử dụng:</strong> {result.context}</p>
              )}
            </div>
          ),
          width: 600
        });
      } else {
        message.error('Có lỗi xảy ra khi gọi AI');
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi gọi AI');
    }
  };

  const handleApproveKnowledge = async (knowledgeId: number) => {
    try {
      // TODO: Implement API call to approve knowledge
      // const result = await virtualAdvisorAdminService.approveKnowledge(knowledgeId);
      
      message.success('Đã phê duyệt kiến thức thành công');
      loadPendingKnowledge();
      loadKnowledge();
      loadKnowledgeMetrics();
    } catch (error) {
      message.error('Có lỗi xảy ra khi phê duyệt kiến thức');
    }
  };

  const handleRejectKnowledge = async (knowledgeId: number) => {
    try {
      // TODO: Implement API call to reject/delete knowledge
      // const result = await virtualAdvisorAdminService.deleteKnowledge(knowledgeId);
      
      message.success('Đã từ chối kiến thức');
      loadPendingKnowledge();
      loadKnowledgeMetrics();
    } catch (error) {
      message.error('Có lỗi xảy ra khi từ chối kiến thức');
    }
  };

  // Document columns
  const documentColumns: ColumnsType<Document> = [
    {
      title: 'Tiêu đề',
      dataIndex: 'Title',
      key: 'Title',
      ellipsis: true,
    },
    {
      title: 'Tên file',
      dataIndex: 'FileName',
      key: 'FileName',
      width: 200,
    },
    {
      title: 'Kích thước',
      dataIndex: 'FileSize',
      key: 'FileSize',
      width: 100,
      render: (size: number) => `${(size / 1024 / 1024).toFixed(2)} MB`,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'Status',
      width: 120,
      render: (status: Document['Status']) => {
        const statusMap: Record<Document['Status'], { text: string; badge: 'default' | 'processing' | 'success' | 'error' | 'warning' }> = {
          uploaded: { text: 'Đã tải lên', badge: 'default' },
          processing: { text: 'Đang xử lý', badge: 'processing' },
          processed: { text: 'Đã xử lý', badge: 'success' },
          awaiting_review: { text: 'Chờ duyệt kiến thức', badge: 'warning' },
          error: { text: 'Lỗi', badge: 'error' }
        };
        const statusInfo = statusMap[status] || { text: status, badge: 'default' };
        return <Badge status={statusInfo.badge} text={statusInfo.text} />;
      },
    },
    {
      title: 'File Search',
      dataIndex: 'GoogleFileId',
      key: 'GoogleFileId',
      width: 150,
      render: (googleFileId: string | undefined, record: Document) => {
        if (googleFileId) {
          return (
            <Tooltip title={`Đã đồng bộ: ${record.FileSearchStoreName || 'N/A'}`}>
              <Tag icon={<CheckCircleOutlined />} color="success">
                Đã đồng bộ
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tooltip title="Chưa đồng bộ lên File Search">
            <Tag icon={<ExclamationCircleOutlined />} color="default">
              Chưa đồng bộ
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString('vi-VN'),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 280,
      render: (_, record) => (
        <Space>
          {record.Status === 'uploaded' && (
            <Button
              type="primary"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleProcessDocument(record.DocumentID)}
            >
              Xử lý
            </Button>
          )}
          {record.Status === 'processed' && !record.GoogleFileId && (
            <Tooltip title="Đồng bộ tài liệu lên File Search để AI có thể trả lời câu hỏi từ tài liệu này">
              <Button
                type="default"
                size="small"
                icon={<FileSearchOutlined />}
                onClick={() => handleSyncToFileSearch(record.DocumentID)}
              >
                Sync File Search
              </Button>
            </Tooltip>
          )}
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedDocument(record);
              loadDocumentChunks(record.DocumentID);
              setDrawerVisible(true);
            }}
          >
            Xem
          </Button>
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa tài liệu này?"
            onConfirm={() => handleDeleteDocument(record.DocumentID)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Knowledge columns
  const knowledgeColumns: ColumnsType<Knowledge> = [
    {
      title: 'Tiêu đề',
      dataIndex: 'Title',
      key: 'Title',
      ellipsis: true,
    },
    {
      title: 'Danh mục',
      dataIndex: 'Category',
      key: 'Category',
      width: 120,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'Status',
      width: 100,
      render: (status: Knowledge['Status']) => {
        const statusMap: Record<Knowledge['Status'], { text: string; badge: 'default' | 'processing' | 'success' }> = {
          draft: { text: 'Nháp', badge: 'default' },
          pending_review: { text: 'Chờ duyệt', badge: 'processing' },
          published: { text: 'Đã xuất bản', badge: 'success' }
        };

        const info = statusMap[status] || { text: status, badge: 'default' };
        return <Badge status={info.badge} text={info.text} />;
      },
    },
    {
      title: 'Tags',
      dataIndex: 'Tags',
      key: 'Tags',
      width: 200,
      render: (tags: string[] = []) => (
        <Space wrap>
          {tags.map(tag => (
            <Tag key={tag} color="blue">{tag}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString('vi-VN'),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditKnowledgeModal(record)}>
            Sửa
          </Button>
          {record.Status !== 'published' && (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handlePublishKnowledge(record.KnowledgeID)}
            >
              {record.Status === 'pending_review' ? 'Phê duyệt' : 'Xuất bản ngay'}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // Tag columns
  const tagColumns: ColumnsType<Tag> = [
    {
      title: 'Tên tag',
      dataIndex: 'Name',
      key: 'Name',
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString('vi-VN'),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="Bạn có chắc chắn muốn xóa tag này?"
          onConfirm={() => handleDeleteTag(record.TagID)}
          okText="Xóa"
          cancelText="Hủy"
        >
          <Button size="small" danger icon={<DeleteOutlined />}>
            Xóa
          </Button>
        </Popconfirm>
      ),
    },
  ];

  // FAQ columns
  const faqColumns: ColumnsType<FAQ> = [
    {
      title: 'Danh mục',
      dataIndex: 'Category',
      key: 'Category',
      width: 120,
    },
    {
      title: 'Câu hỏi',
      dataIndex: 'Question',
      key: 'Question',
      ellipsis: true,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'Status',
      width: 100,
      render: (status: string) => (
        <Badge color={status === 'published' ? 'green' : 'orange'} text={status === 'published' ? 'Đã xuất bản' : 'Nháp'} />
      ),
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'UpdatedAt',
      key: 'UpdatedAt',
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString('vi-VN'),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />}>
            Sửa
          </Button>
          <Button size="small" icon={<EyeOutlined />}>
            Xem
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <Title level={3}>Quản lý Cố vấn học tập ảo</Title>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={
            <span>
              <FileTextOutlined />
              Tài liệu PDF
            </span>
          } key="documents">
            <div style={{ marginBottom: '16px' }}>
              <Row gutter={16} align="middle">
                <Col flex="auto">
                  <Input
                    placeholder="Tìm kiếm tài liệu..."
                    prefix={<SearchOutlined />}
                    value={documentFilters.search}
                    onChange={(e) => setDocumentFilters({ ...documentFilters, search: e.target.value })}
                  />
                </Col>
                <Col>
                  <Select
                    placeholder="Lọc theo trạng thái"
                    style={{ width: 150 }}
                    value={documentFilters.status}
                    onChange={(value) => setDocumentFilters({ ...documentFilters, status: value })}
                  >
                    <Option value="">Tất cả</Option>
                    <Option value="uploaded">Đã tải lên</Option>
                    <Option value="processing">Đang xử lý</Option>
                    <Option value="processed">Đã xử lý</Option>
                    <Option value="awaiting_review">Chờ duyệt kiến thức</Option>
                    <Option value="error">Lỗi</Option>
                  </Select>
                </Col>
                <Col>
                  <Button icon={<ReloadOutlined />} onClick={loadDocuments}>
                    Làm mới
                  </Button>
                </Col>
              </Row>
            </div>

            <Upload
              customRequest={handleDocumentUpload}
              showUploadList={false}
              accept=".pdf"
            >
              <Button icon={<UploadOutlined />} type="primary">
                Tải lên PDF
              </Button>
            </Upload>

            <Table
              columns={documentColumns}
              dataSource={documents || []}
              rowKey="DocumentID"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: documents?.length || 0,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} tài liệu`,
              }}
              scroll={{ x: 800 }}
            />
          </TabPane>

          <TabPane tab={
            <span>
              <FileTextOutlined />
              Kiến thức
            </span>
          } key="knowledge">
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Kiến thức chờ duyệt"
                    value={knowledgeMetrics.pending_review}
                    prefix={<ClockCircleOutlined />}
                  />
                  <Button
                    type="link"
                    size="small"
                    onClick={() => applyKnowledgeStatusFilter('pending_review')}
                    disabled={!knowledgeMetrics.pending_review}
                  >
                    Xem hàng chờ duyệt
                  </Button>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Kiến thức nháp"
                    value={knowledgeMetrics.draft}
                    prefix={<EditOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Đã xuất bản"
                    value={knowledgeMetrics.published}
                    prefix={<CheckCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Tổng kiến thức"
                    value={knowledgeMetrics.total}
                    prefix={<FileTextOutlined />}
                  />
                </Card>
              </Col>
            </Row>
            <div style={{ marginBottom: '16px' }}>
              <Row gutter={16} align="middle">
                <Col flex="auto">
                  <Input
                    placeholder="Tìm kiếm kiến thức..."
                    prefix={<SearchOutlined />}
                    value={knowledgeFilters.search}
                    onChange={handleKnowledgeSearchChange}
                  />
                </Col>
                <Col>
                  <Select
                    placeholder="Lọc theo danh mục"
                    style={{ width: 150 }}
                    value={knowledgeFilters.category}
                    onChange={(value) => handleKnowledgeCategoryChange(value as string)}
                  >
                    <Option value="">Tất cả</Option>
                    <Option value="quy-che-dao-tao">Quy chế đào tạo</Option>
                    <Option value="che-do-sinh-vien">Chế độ sinh viên</Option>
                    <Option value="nghia-vu-sinh-vien">Nghĩa vụ sinh viên</Option>
                    <Option value="diem-danh">Điểm danh</Option>
                    <Option value="hoc-phi">Học phí</Option>
                  </Select>
                </Col>
                <Col>
                  <Select
                    placeholder="Lọc theo trạng thái"
                    style={{ width: 150 }}
                    value={knowledgeFilters.status}
                    onChange={(value) => applyKnowledgeStatusFilter(value as string)}
                  >
                    <Option value="">Tất cả</Option>
                    <Option value="draft">Nháp</Option>
                    <Option value="pending_review">Chờ duyệt</Option>
                    <Option value="published">Đã xuất bản</Option>
                  </Select>
                </Col>
                <Col>
                  <Button type="primary" icon={<PlusOutlined />} onClick={openCreateKnowledgeModal}>
                    Thêm kiến thức
                  </Button>
                </Col>
              </Row>
            </div>

            <Table
              columns={knowledgeColumns}
              dataSource={knowledge || []}
              rowKey="KnowledgeID"
              loading={loading}
              onChange={(paginationConfig) => handleKnowledgeTableChange(paginationConfig)}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: knowledgeTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} kiến thức`,
              }}
              scroll={{ x: 1000 }}
            />
          </TabPane>

          <TabPane tab={
            <span>
              <FileTextOutlined />
              Tags
            </span>
          } key="tags">
            <div style={{ marginBottom: '16px' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => tagForm.resetFields()}>
                Thêm tag
              </Button>
            </div>

            <Form form={tagForm} layout="inline" onFinish={handleCreateTag} style={{ marginBottom: '16px' }}>
              <Form.Item name="name" rules={[{ required: true, message: 'Vui lòng nhập tên tag' }]}>
                <Input placeholder="Tên tag" style={{ width: 200 }} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">Thêm</Button>
              </Form.Item>
            </Form>

            <Table
              columns={tagColumns}
              dataSource={tags}
              rowKey="TagID"
              loading={loading}
              pagination={false}
            />
          </TabPane>

          <TabPane tab={
            <span>
              <FileTextOutlined />
              FAQ
            </span>
          } key="faqs">
            <div style={{ marginBottom: '16px' }}>
              <Row gutter={16} align="middle">
                <Col flex="auto">
                  <Select
                    placeholder="Lọc theo danh mục"
                    style={{ width: 200 }}
                    value={faqFilters.category}
                    onChange={(value) => setFaqFilters({ ...faqFilters, category: value })}
                  >
                    <Option value="">Tất cả</Option>
                    <Option value="quy-che-dao-tao">Quy chế đào tạo</Option>
                    <Option value="che-do-sinh-vien">Chế độ sinh viên</Option>
                    <Option value="nghia-vu-sinh-vien">Nghĩa vụ sinh viên</Option>
                    <Option value="diem-danh">Điểm danh</Option>
                    <Option value="hoc-phi">Học phí</Option>
                  </Select>
                </Col>
                <Col>
                  <Select
                    placeholder="Lọc theo trạng thái"
                    style={{ width: 150 }}
                    value={faqFilters.status}
                    onChange={(value) => setFaqFilters({ ...faqFilters, status: value })}
                  >
                    <Option value="">Tất cả</Option>
                    <Option value="draft">Nháp</Option>
                    <Option value="published">Đã xuất bản</Option>
                  </Select>
                </Col>
                <Col>
                  <Button type="primary" icon={<PlusOutlined />}>
                    Thêm FAQ
                  </Button>
                </Col>
              </Row>
            </div>

            <Table
              columns={faqColumns}
              dataSource={faqs || []}
              rowKey="FAQID"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: faqs?.length || 0,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} FAQ`,
              }}
            />
          </TabPane>

          <TabPane tab={
            <span>
              <SettingOutlined />
              Cài đặt
            </span>
          } key="settings">
            <Form
              form={settingsForm}
              layout="vertical"
              onFinish={handleUpdateSettings}
              style={{ maxWidth: 600 }}
            >
              <Form.Item label="Model AI mặc định" name="default_model">
                <Select>
                  <Option value="gemini-2.5-flash">Gemini 2.5 Flash</Option>
                  <Option value="gemini-pro">Gemini Pro</Option>
                  <Option value="gemini-1.5-flash">Gemini 1.5 Flash</Option>
                  <Option value="gemini-1.5-pro">Gemini 1.5 Pro</Option>
                </Select>
              </Form.Item>

              <Form.Item label="Temperature" name="temperature">
                <InputNumber min={0} max={2} step={0.1} />
              </Form.Item>

              <Form.Item label="Top P" name="top_p">
                <InputNumber min={0} max={1} step={0.05} />
              </Form.Item>

              <Form.Item label="Max Tokens" name="max_tokens">
                <InputNumber min={100} max={8192} step={100} />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Cập nhật cài đặt
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab={
            <span>
              <PlayCircleOutlined />
              Playground
            </span>
          } key="playground">
            <Alert
              message="Playground để kiểm thử AI"
              description="Nhập câu hỏi và chọn context để kiểm thử cách AI trả lời dựa trên kiến thức có sẵn."
              type="info"
              style={{ marginBottom: '16px' }}
            />

            <Form form={playgroundForm} onFinish={handlePlaygroundAsk} layout="vertical">
              <Form.Item
                label="Câu hỏi"
                name="question"
                rules={[{ required: true, message: 'Vui lòng nhập câu hỏi' }]}
              >
                <TextArea rows={4} placeholder="Nhập câu hỏi để kiểm thử AI..." />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Tags liên quan" name="tags">
                    <Select mode="multiple" placeholder="Chọn tags liên quan">
                      {tags.map(tag => (
                        <Option key={tag.TagID} value={tag.Name}>{tag.Name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Danh mục" name="categories">
                    <Select mode="multiple" placeholder="Chọn danh mục">
                      <Option value="quy-che-dao-tao">Quy chế đào tạo</Option>
                      <Option value="che-do-sinh-vien">Chế độ sinh viên</Option>
                      <Option value="nghia-vu-sinh-vien">Nghĩa vụ sinh viên</Option>
                      <Option value="diem-danh">Điểm danh</Option>
                      <Option value="hoc-phi">Học phí</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Tài liệu tham khảo" name="documentIds">
                <Select mode="multiple" placeholder="Chọn tài liệu để làm context">
                  {documents.filter(d => d.Status === 'processed').map(doc => (
                    <Option key={doc.DocumentID} value={doc.DocumentID}>{doc.Title}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" icon={<PlayCircleOutlined />}>
                  Kiểm thử AI
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          {/* Knowledge Approval Tab */}
          <TabPane tab={
            <span>
              <FileSearchOutlined />
              Duyệt kiến thức
            </span>
          } key="approval">
            <Alert
              message="Phê duyệt kiến thức"
              description="Duyệt các kiến thức được tạo tự động từ tài liệu PDF hoặc do người dùng tạo."
              type="info"
              style={{ marginBottom: '16px' }}
            />

            <Table
              dataSource={pendingKnowledge || []}
              loading={loading}
              rowKey="KnowledgeID"
              columns={[
                {
                  title: 'Tiêu đề',
                  dataIndex: 'Title',
                  key: 'title',
                  width: '30%',
                  render: (text: string) => <Typography.Text strong>{text}</Typography.Text>
                },
                {
                  title: 'Danh mục',
                  dataIndex: 'Category',
                  key: 'category',
                  width: '15%',
                  render: (text: string) => <Tag color="blue">{text}</Tag>
                },
                {
                  title: 'Tags',
                  dataIndex: 'Tags',
                  key: 'tags',
                  width: '20%',
                  render: (tags: string) => (
                    <>
                      {tags && tags.split(',').map((tag: string, index: number) => (
                        <Tag key={index} style={{ marginBottom: 4 }}>{tag.trim()}</Tag>
                      ))}
                    </>
                  )
                },
                {
                  title: 'Nội dung',
                  dataIndex: 'Content',
                  key: 'content',
                  width: '25%',
                  ellipsis: true,
                  render: (text: string) => (
                    <Tooltip title={text}>
                      <Typography.Text ellipsis>{text?.substring(0, 100)}...</Typography.Text>
                    </Tooltip>
                  )
                },
                {
                  title: 'Thao tác',
                  key: 'action',
                  width: '10%',
                  render: (_: any, record: any) => (
                    <Space>
                      <Tooltip title="Phê duyệt">
                        <Button
                          type="primary"
                          size="small"
                          icon={<CheckCircleOutlined />}
                          onClick={() => handleApproveKnowledge(record.KnowledgeID)}
                        />
                      </Tooltip>
                      <Tooltip title="Chỉnh sửa">
                        <Button
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setSelectedKnowledge(record);
                            knowledgeForm.setFieldsValue(record);
                            setIsKnowledgeEditing(true);
                            setModalVisible(true);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Từ chối">
                        <Popconfirm
                          title="Bạn có chắc muốn từ chối kiến thức này?"
                          onConfirm={() => handleRejectKnowledge(record.KnowledgeID)}
                        >
                          <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                          />
                        </Popconfirm>
                      </Tooltip>
                    </Space>
                  )
                }
              ]}
            />
          </TabPane>

          {/* Chat Logs Tab */}
          <TabPane tab={
            <span>
              <CommentOutlined />
              Lịch sử chat
            </span>
          } key="chatlogs">
            <Alert
              message="Lịch sử phản hồi và feedback"
              description="Xem lịch sử các cuộc trò chuyện, câu hỏi, phản hồi của AI và feedback từ người dùng."
              type="info"
              style={{ marginBottom: '16px' }}
            />

            <Table
              dataSource={chatLogs}
              loading={loading}
              rowKey="id"
              columns={[
                {
                  title: 'Sinh viên',
                  dataIndex: 'studentName',
                  key: 'studentName',
                  width: '15%'
                },
                {
                  title: 'Câu hỏi',
                  dataIndex: 'message',
                  key: 'message',
                  width: '25%',
                  render: (text: string) => (
                    <Tooltip title={text}>
                      <Typography.Text ellipsis>{text}</Typography.Text>
                    </Tooltip>
                  )
                },
                {
                  title: 'Phản hồi',
                  dataIndex: 'response',
                  key: 'response',
                  width: '30%',
                  render: (text: string) => (
                    <Tooltip title={text}>
                      <Typography.Text ellipsis>{text?.substring(0, 100)}...</Typography.Text>
                    </Tooltip>
                  )
                },
                {
                  title: 'Độ tin cậy',
                  dataIndex: 'confidence',
                  key: 'confidence',
                  width: '10%',
                  render: (confidence: number) => (
                    <Tag color={confidence >= 0.8 ? 'green' : confidence >= 0.6 ? 'orange' : 'red'}>
                      {(confidence * 100).toFixed(0)}%
                    </Tag>
                  )
                },
                {
                  title: 'Nguồn',
                  dataIndex: 'sources',
                  key: 'sources',
                  width: '10%',
                  render: (sources: any[]) => (
                    <Typography.Text>{sources?.length || 0} nguồn</Typography.Text>
                  )
                },
                {
                  title: 'Feedback',
                  dataIndex: 'feedback',
                  key: 'feedback',
                  width: '10%',
                  render: (feedback: string) => (
                    feedback === 'positive' ? (
                      <Tag color="success" icon={<LikeOutlined />}>Hữu ích</Tag>
                    ) : feedback === 'negative' ? (
                      <Tag color="error" icon={<DislikeOutlined />}>Không hữu ích</Tag>
                    ) : (
                      <Tag color="default">Chưa đánh giá</Tag>
                    )
                  )
                },
                {
                  title: 'Thời gian',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  width: '12%',
                  render: (date: Date) => new Date(date).toLocaleString('vi-VN')
                }
              ]}
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ padding: '16px', background: '#fafafa', borderRadius: 8 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Typography.Title level={5}>Câu hỏi:</Typography.Title>
                        <Typography.Paragraph>{record.message}</Typography.Paragraph>
                      </Col>
                      <Col span={12}>
                        <Typography.Title level={5}>Phản hồi:</Typography.Title>
                        <Typography.Paragraph>{record.response}</Typography.Paragraph>
                      </Col>
                    </Row>
                    {record.sources && record.sources.length > 0 && Array.isArray(record.sources) && (
                      <div style={{ marginTop: 16 }}>
                        <Typography.Title level={5}>Nguồn tham khảo:</Typography.Title>
                        <List
                          size="small"
                          dataSource={record.sources}
                          renderItem={(source: any) => (
                            <List.Item>
                              <Typography.Text>[Nguồn {source.sourceNumber}] {source.title}</Typography.Text>
                            </List.Item>
                          )}
                        />
                      </div>
                    )}
                    <Divider />
                    <Row gutter={16}>
                      <Col span={6}>
                        <Typography.Text type="secondary">Model: {record.model}</Typography.Text>
                      </Col>
                      <Col span={6}>
                        <Typography.Text type="secondary">Session ID: {record.sessionId}</Typography.Text>
                      </Col>
                      <Col span={6}>
                        <Typography.Text type="secondary">Độ tin cậy: {(record.confidence * 100).toFixed(1)}%</Typography.Text>
                      </Col>
                    </Row>
                  </div>
                )
              }}
            />
          </TabPane>

          {/* Debug Search Tab */}
          <TabPane tab={
            <span>
              <BugOutlined />
              Debug Search
            </span>
          } key="debugsearch">
            <Alert
              message="Công cụ debug tìm kiếm"
              description="Kiểm tra cách hệ thống tìm kiếm và xếp hạng kiến thức dựa trên truy vấn."
              type="info"
              style={{ marginBottom: '16px' }}
            />

            <Card title="Truy vấn tìm kiếm" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={18}>
                  <Input.Search
                    placeholder="Nhập câu hỏi hoặc truy vấn tìm kiếm..."
                    value={debugSearchQuery}
                    onChange={(e) => setDebugSearchQuery(e.target.value)}
                    onSearch={handleDebugSearch}
                    enterButton="Tìm kiếm"
                    size="large"
                    loading={loadingDebugSearch}
                  />
                </Col>
                <Col span={6}>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      setDebugSearchQuery('');
                      setDebugSearchResults([]);
                    }}
                    size="large"
                  >
                    Xóa kết quả
                  </Button>
                </Col>
              </Row>
            </Card>

            {debugSearchResults && debugSearchResults.length > 0 && (
              <Card title={`Kết quả tìm kiếm (${debugSearchResults.length})`}>
                <List
                  dataSource={debugSearchResults}
                  renderItem={(item: any, index: number) => (
                    <List.Item key={item.knowledgeId}>
                      <List.Item.Meta
                        avatar={
                          <Avatar style={{ backgroundColor: '#1890ff', fontSize: 18 }}>
                            #{index + 1}
                          </Avatar>
                        }
                        title={
                          <div>
                            <Typography.Text strong>{item.title}</Typography.Text>
                            <Tag color="blue" style={{ marginLeft: 8 }}>{item.category}</Tag>
                            <Tag color="green" style={{ marginLeft: 4 }}>
                              Score: {(item.score * 100).toFixed(0)}%
                            </Tag>
                          </div>
                        }
                        description={
                          <div>
                            <Typography.Paragraph ellipsis={{ rows: 2 }}>
                              {item.content}
                            </Typography.Paragraph>
                            <Space wrap style={{ marginTop: 8 }}>
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                Keyword: {(item.keywordScore * 100).toFixed(0)}%
                              </Typography.Text>
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                Similarity: {(item.similarityScore * 100).toFixed(0)}%
                              </Typography.Text>
                              {item.tags && item.tags.map((tag: string) => (
                                <Tag key={tag} style={{ fontSize: 11 }}>{tag}</Tag>
                              ))}
                            </Space>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            )}

            {debugSearchResults && debugSearchResults.length === 0 && debugSearchQuery && !loadingDebugSearch && (
              <Empty description="Không tìm thấy kết quả phù hợp" />
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* Document Details Drawer */}
      <Drawer
        title={`Chi tiết tài liệu: ${selectedDocument?.Title}`}
        width={800}
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {selectedDocument && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Tiêu đề">{selectedDocument.Title}</Descriptions.Item>
              <Descriptions.Item label="Tên file">{selectedDocument.FileName}</Descriptions.Item>
              <Descriptions.Item label="Kích thước">{(selectedDocument.FileSize / 1024 / 1024).toFixed(2)} MB</Descriptions.Item>
              <Descriptions.Item label="Trạng thái">
                {(() => {
                  const statusMap: Record<Document['Status'], { text: string; badge: 'default' | 'processing' | 'success' | 'error' | 'warning' }> = {
                    uploaded: { text: 'Đã tải lên', badge: 'default' },
                    processing: { text: 'Đang xử lý', badge: 'processing' },
                    processed: { text: 'Đã xử lý', badge: 'success' },
                    awaiting_review: { text: 'Chờ duyệt kiến thức', badge: 'warning' },
                    error: { text: 'Lỗi', badge: 'error' }
                  };
                  const info = statusMap[selectedDocument.Status] || { text: selectedDocument.Status, badge: 'default' };
                  return <Badge status={info.badge} text={info.text} />;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày tạo">
                {new Date(selectedDocument.CreatedAt).toLocaleString('vi-VN')}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày xử lý">
                {selectedDocument.ProcessedAt ? new Date(selectedDocument.ProcessedAt).toLocaleString('vi-VN') : 'Chưa xử lý'}
              </Descriptions.Item>
            </Descriptions>

            {selectedDocument.Status === 'processed' && (
              <div style={{ marginTop: '16px' }}>
                <Title level={5}>Nội dung đã xử lý ({documentChunks?.length || 0} đoạn)</Title>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {documentChunks && documentChunks.map((chunk, index) => (
                    <div key={chunk.ChunkID} style={{ marginBottom: '8px', padding: '8px', background: '#f6f8fa', borderRadius: '4px' }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                        Đoạn {index + 1} (Trang {chunk.PageNumber})
                      </div>
                      <div>{chunk.Content.substring(0, 200)}...</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedDocument.Status === 'error' && selectedDocument.ErrorMessage && (
              <Alert
                message="Lỗi xử lý tài liệu"
                description={selectedDocument.ErrorMessage}
                type="error"
                style={{ marginTop: '16px' }}
              />
            )}
          </div>
        )}
      </Drawer>

      {/* Create Knowledge Modal */}
      <Modal
        title={isKnowledgeEditing ? 'Chỉnh sửa kiến thức' : 'Thêm kiến thức mới'}
        visible={modalVisible}
        onCancel={closeKnowledgeModal}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form form={knowledgeForm} onFinish={handleSubmitKnowledge} layout="vertical">
          <Form.Item
            label="Tiêu đề"
            name="title"
            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Danh mục"
            name="category"
            rules={[{ required: true, message: 'Vui lòng chọn danh mục' }]}
          >
            <Select>
              <Option value="quy-che-dao-tao">Quy chế đào tạo</Option>
              <Option value="che-do-sinh-vien">Chế độ sinh viên</Option>
              <Option value="nghia-vu-sinh-vien">Nghĩa vụ sinh viên</Option>
              <Option value="diem-danh">Điểm danh</Option>
              <Option value="hoc-phi">Học phí</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Trạng thái" name="status">
            <Select disabled={!isKnowledgeEditing}>
              <Option value="draft">Nháp</Option>
              <Option value="pending_review">Chờ duyệt</Option>
              <Option value="published">Đã xuất bản</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Nội dung"
            name="content"
            rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}
          >
            <TextArea rows={8} />
          </Form.Item>

          <Form.Item label="Tags" name="tags">
            <Select mode="tags" placeholder="Nhập tags và nhấn Enter">
              {tags.map(tag => (
                <Option key={tag.TagID} value={tag.Name}>{tag.Name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Ghi chú phê duyệt" name="notes">
            <TextArea rows={3} placeholder="Ghi chú cho việc phê duyệt (tùy chọn)" />
          </Form.Item>

          <Form.Item label="Tài liệu nguồn" name="documentId">
            <Select placeholder="Chọn tài liệu PDF nguồn (tùy chọn)" allowClear>
              {documents
                .filter(d => d.Status === 'processed' || d.Status === 'awaiting_review')
                .map(doc => (
                <Option key={doc.DocumentID} value={doc.DocumentID}>{doc.Title}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {isKnowledgeEditing ? 'Lưu thay đổi' : 'Tạo kiến thức'}
              </Button>
              <Button onClick={closeKnowledgeModal}>
                Hủy
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VirtualAdvisorManagementPage;
